import { useCallback, useMemo } from 'react';

import type { SymbolKey } from '@/entities/symbol';
import {
  addSymbol,
  addSymbols,
  deleteSymbol,
  deleteSymbols,
  toggleSymbol
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';

const useSelectionFunctions = () => {
  const dispatch = useAppDispatch();
  const symbols = useAppSelector(state => state.symbolSelector.symbols);
  const isForwardMode = useAppSelector(state => state.symbolSelector.isForwardMode);

  const toggleSymbolInSelection = useCallback(
    (symbol: SymbolKey): void => {
      dispatch(toggleSymbol({ symbol }));
    },
    [dispatch]
  );

  // Create a fast lookup Set for selected symbols to avoid O(n) scans in every cell.
  const selectedKeySet = useMemo(() => {
    const toKey = (s: SymbolKey) =>
      isForwardMode
        ? `${s.Code}|${s.PeriodType}|${s.TimeRef}`
        : `${s.Code}|${s.Period}|${s.PeriodType}|${s.TimeRef}`;
    const set = new Set<string>();
    for (const s of symbols) set.add(toKey(s));
    return { set, toKey } as const;
  }, [symbols, isForwardMode]);

  const isSymbolSelected = useCallback(
    (selectedSymbol: SymbolKey | undefined) => {
      if (!selectedSymbol) return false;
      return selectedKeySet.set.has(selectedKeySet.toKey(selectedSymbol));
    },
    [selectedKeySet]
  );

  const areAllSymbolsSelected = useCallback(
    (arr: SymbolKey[]) => {
      return arr.every(symbol => selectedKeySet.set.has(selectedKeySet.toKey(symbol)));
    },
    [selectedKeySet]
  );

  const addSymbolToSelection = (symbol: SymbolKey) => {
    dispatch(addSymbol({ symbol }));
  };

  const addSymbolsToSelection = (symbols: SymbolKey[]) => {
    dispatch(addSymbols({ symbols }));
  };

  const removeSymbolsFromSelection = (symbols: Array<SymbolKey>) => {
    dispatch(deleteSymbols({ symbols }));
  };

  const removeSymbolFromSelection = (symbol: SymbolKey) => {
    if (symbol.Code.length) {
      dispatch(deleteSymbol({ symbol }));
    }
  };

  const noneSelected = symbols.length < 1;

  return {
    toggleSymbolInSelection,
    addSymbolsToSelection,
    areAllSymbolsSelected,
    removeSymbolFromSelection,
    removeSymbolsFromSelection,
    isSymbolSelected,
    addSymbolToSelection,
    noneSelected
  };
};

export default useSelectionFunctions;
